<script>
'use strict';

const analytics = require('../../lib/analytics');

module.exports = {
  name: 'Survey',
  data() {
    return {
      showSuccess: false,
    };
  },
  computed: {
    searchValue() {
      return this.$store.state.searchValue;
    },
    productFilters() {
      return this.$store.state.productFilters;
    },
    showSurvey() {
      return this.$store.state.showSurvey;
    },
    activeTab() {
      return this.$store.state.activeTab;
    },
  },
  watch: {
    /**
     * Resets the thank-you state each time the survey becomes visible so a
     * returning user always lands on the voting view, not a stale thank-you.
     * @param {boolean} val - New showSurvey value.
     */
    showSurvey(val) {
      if (val) this.showSuccess = false;
    },
  },
  methods: {
    /**
     * Closes the survey dialog.
     */
    onCloseClick() {
      this.$store.commit('setShowSurvey', false);
    },

    /**
     * Records user feedback via GTM and transitions to the thank-you state.
     * The survey link is hydrated with the current query and price range so the
     * Google Form pre-fills the relevant context.
     * @param {string} feedback - 'good' or 'bad'.
     */
    onThumbClick(feedback) {
      analytics.fireGiftScoutFeedbackEvent({
        feedback,
        query: this.searchValue,
        activeTab: this.activeTab,
        filters: this.productFilters,
      });

      this.showSuccess = true;

      this.$nextTick(() => {
        const surveyLink = this.$refs.surveyLink;
        const { price, priceInit } = this.productFilters;
        const min = price.min !== null ? price.min : priceInit.min;
        const max = price.max !== null ? price.max : priceInit.max;
        const budget = min && max ? `$${min}-$${max}` : 'none';
        const urlSearchParams = new URLSearchParams(
          `usp=pp_url&entry.145917890=${
            this.searchValue
          };+budget+${budget}&entry.246783320=${feedback === 'bad' ? '1' : '2'}`
        );

        surveyLink.setAttribute(
          'href',
          `${surveyLink.dataset.surveyHref}?${urlSearchParams}`
        );
      });
    },
  },
};
</script>

<template>
  <div v-show="showSurvey" class="search-results-survey">
    <div v-if="showSuccess" class="search-results-survey-thank-you">
      <p class="search-results-survey-title">Thanks for your feedback!&nbsp;</p>
      <a
        ref="surveyLink"
        href=""
        data-survey-href="https://docs.google.com/forms/d/e/1FAIpQLSeYD3o-Yb5VOB9oicxkILLp191Hq_gr9lUp4pVrYkwAbBrp7g/viewform"
        target="_blank"
        class="search-results-survey-link"
        ><span class="search-results-survey-link-content"></span></a
      >.
    </div>

    <div v-else class="search-results-survey-content">
      <p class="search-results-survey-title">How are the results?</p>
      <div class="search-results-survey-thumbs-container">
        <button
          class="search-results-survey-thumbs search-results-survey-thumbs-down"
          aria-label="Thumbs Down"
          @click="onThumbClick('bad')"
        >
          <svg
            class="search-results-survey-thumbs-svg"
            width="23"
            height="25"
            viewBox="0 0 19 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M2.4676e-05 12.1971C2.4676e-05 13.1594 0.785364 13.9396 1.75412 13.9396L7.06489 13.9101C7.4432 13.908 7.71841 14.2697 7.61225 14.6329C7.3595 15.4975 6.98748 16.9771 6.98748 18.1952C6.98748 20.0401 7.72722 20.9164 8.78624 20.9164C9.63951 20.9164 10.3546 20.2757 10.5619 19.1177C11.097 16.1286 14.5469 13.2832 16.4567 13.2832H18.028C18.2631 13.2832 18.4537 13.0926 18.4538 12.8574V1.57894C18.4538 1.37702 18.3131 1.20286 18.1153 1.16217C16.8356 0.898923 12.1383 0 7.66062 0H5.84409H4.02757C3.05881 0 2.27348 0.780107 2.27348 1.74245C2.27348 2.70479 3.05881 3.48487 4.02757 3.48487H2.78997C1.82119 3.48487 1.03585 4.26498 1.03585 5.22732C1.03585 6.18966 1.82119 6.96977 2.78997 6.96977H2.31528C1.34652 6.96977 0.561182 7.74987 0.561182 8.71219C0.561182 9.6745 1.34652 10.4546 2.31528 10.4546H1.75409C0.785339 10.4546 2.4676e-05 11.2348 2.4676e-05 12.1971Z"
            />
          </svg>
        </button>

        <button
          class="search-results-survey-thumbs search-results-survey-thumbs-up"
          aria-label="Thumbs Up"
          @click="onThumbClick('good')"
        >
          <svg
            class="search-results-survey-thumbs-svg"
            width="23"
            height="25"
            viewBox="0 0 19 21"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M18.4519 8.71844C18.4519 7.75622 17.6666 6.97617 16.698 6.97617L11.3877 7.00558C11.0095 7.00767 10.7343 6.64603 10.8404 6.28294C11.0932 5.41835 11.4651 3.93895 11.4651 2.72095C11.4651 0.876256 10.7255 0 9.66654 0C8.81336 0 8.09829 0.640673 7.89104 1.7986C7.35604 4.7874 3.90651 7.63246 1.99688 7.63246H0.425742C0.190603 7.63246 0 7.82308 0 8.0582V19.3355C0 19.5374 0.140688 19.7115 0.338447 19.7522C1.61799 20.0155 6.31479 20.9143 10.7921 20.9143H14.4247C15.3934 20.9143 16.1787 20.1342 16.1787 19.172C16.1787 18.2097 15.3934 17.4297 14.4247 17.4297H15.6622C16.6309 17.4297 17.4162 16.6497 17.4162 15.6875C17.4162 14.7252 16.6309 13.9452 15.6622 13.9452H16.1369C17.1055 13.9452 17.8908 13.1652 17.8908 12.203C17.8908 11.2407 17.1055 10.4607 16.1369 10.4607H16.698C17.6666 10.4607 18.4519 9.68068 18.4519 8.71844Z"
            />
          </svg>
        </button>
      </div>
    </div>

    <button
      class="search-results-survey-close"
      aria-label="Close"
      @click="onCloseClick"
    >
      <img
        class="search-results-survey-close-img"
        src="/media/components/search-remix/x.svg"
      />
    </button>
  </div>
</template>
